// Priority-pricing model for the Maharaj Ji premium booking flow.
// The fee is not a fixed per-session rate — it's set by how soon the client
// wants the audience: sooner turnaround costs more, matching a concierge /
// expedited-service model.

export const MAX_VISIBILITY_DAYS = 46

// Bookable dates fall into three fixed slabs only — every other "days until
// session" value is unbookable, not just differently priced.
//
// These are the FALLBACK values: live prices/windows come from
// GET /astrologer/pricing-slabs via useMaharajTiers(), which overlays
// price/minDays/maxDays onto this config (keys, labels and icons stay
// local). Every helper below takes an optional `tiers` argument so callers
// holding the live list get consistent fee/calendar behaviour; the default
// keeps old static call sites working.
export const MAHARAJ_TIERS = [
  { key: 'zap', label: 'Zap - Urgent', icon: 'zap', price: 21000, minDays: 0, maxDays: 2 },
  { key: 'star', label: 'Star - Priority', icon: 'star', price: 15000, minDays: 15, maxDays: 16, recommended: true },
  { key: 'standard', label: 'Standard', icon: 'thumb', price: 11000, minDays: 45, maxDays: 46 },
]

/** Whole-day difference between two dates (ignores time-of-day). */
export function daysBetween(from, to) {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.round((b - a) / 86400000)
}

/** Which pricing tier a given "days until session" value falls into — null
 * for any day outside the three bookable slabs. */
export function tierForDaysUntil(daysUntil, tiers = MAHARAJ_TIERS) {
  return tiers.find((t) => daysUntil >= t.minDays && daysUntil <= t.maxDays) ?? null
}

/** Whether a "days until session" value is one of the bookable slabs. */
export function isAllowedDaysUntil(daysUntil, tiers = MAHARAJ_TIERS) {
  return tierForDaysUntil(daysUntil, tiers) !== null
}

/** Calendar horizon — the far edge of the last bookable window. */
export function maxVisibilityDays(tiers = MAHARAJ_TIERS) {
  return Math.max(...tiers.map((t) => t.maxDays))
}

/** Lowest tier price, for "Starting from ₹…" labels. */
export function startingPrice(tiers = MAHARAJ_TIERS) {
  return Math.min(...tiers.map((t) => t.price))
}
